import React, { useState } from 'react';
import { UploadIcon, Trash2Icon } from 'lucide-react';
import {
  isValidNoteFile,
  getFileIcon,
  formatFileSize,
} from '../../lib/storage/note-files';

interface FileUploadProps {
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
  initialFiles?: File[];
}

export function FileUpload({ onFilesChange, maxFiles = 5, initialFiles = [] }: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<File[]>(initialFiles);
  const [errors, setErrors] = useState<string[]>([]);

  // Sync with initialFiles when they change (e.g., auto-downloaded media)
  React.useEffect(() => {
    console.log('FileUpload initialFiles effect:', {
      initialFilesLength: initialFiles.length,
      currentFilesLength: files.length,
      initialFiles: initialFiles.map(f => ({ name: f.name, size: f.size }))
    });

    if (initialFiles.length > 0 && files.length === 0) {
      console.log('Setting files from initialFiles');
      setFiles(initialFiles);
    }
  }, [initialFiles, files.length]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const addFiles = (newFiles: FileList | null) => {
    if (!newFiles) return;

    const fileArray = Array.from(newFiles);
    const validFiles: File[] = [];
    const newErrors: string[] = [];

    // Check if adding these files would exceed max
    if (files.length + fileArray.length > maxFiles) {
      newErrors.push(`Maximum ${maxFiles} files allowed`);
      setErrors(newErrors);
      return;
    }

    // Validate each file
    fileArray.forEach((file) => {
      const validation = isValidNoteFile(file);
      if (validation.valid) {
        // Check for duplicates
        const isDuplicate = files.some((f) => f.name === file.name);
        if (!isDuplicate) {
          validFiles.push(file);
        } else {
          newErrors.push(`${file.name} is already added`);
        }
      } else {
        newErrors.push(`${file.name}: ${validation.error}`);
      }
    });

    if (validFiles.length > 0) {
      const updatedFiles = [...files, ...validFiles];
      setFiles(updatedFiles);
      onFilesChange(updatedFiles);
    }

    setErrors(newErrors);

    // Clear errors after 5 seconds
    if (newErrors.length > 0) {
      setTimeout(() => setErrors([]), 5000);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(e.target.files);
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  const clearAll = () => {
    setFiles([]);
    onFilesChange([]);
    setErrors([]);
  };

  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
          isDragging
            ? 'border-[#5B8E87] bg-[#5B8E87] bg-opacity-5'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <UploadIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="font-medium text-[#212121] mb-1">
          Drop files here or click to browse
        </p>
        <p className="text-sm text-gray-600 mb-3">
          Documents, images, videos, audio (max 25MB each)
        </p>
        <input
          type="file"
          accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.webp,.mp4,.mov,.mp3,.ogg"
          onChange={handleFileSelect}
          className="hidden"
          id="file-upload"
          multiple
        />
        <label
          htmlFor="file-upload"
          className="inline-block px-5 py-2.5 bg-[#5B8E87] text-white rounded-lg font-medium hover:bg-[#4F7A74] transition-colors cursor-pointer"
        >
          Browse Files
        </label>
      </div>

      {/* Error Messages */}
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          {errors.map((error, idx) => (
            <p key={idx} className="text-sm text-red-600">
              {error}
            </p>
          ))}
        </div>
      )}

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700">
              {files.length} file{files.length > 1 ? 's' : ''} selected
            </p>
            <button
              onClick={clearAll}
              className="text-sm text-red-600 hover:text-red-700 font-medium"
            >
              Clear All
            </button>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {files.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center justify-between bg-gray-50 rounded-lg p-3 group hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="text-2xl flex-shrink-0">
                    {getFileIcon(file.name)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#212121] truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-600">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="text-gray-400 hover:text-red-600 transition-colors flex-shrink-0 ml-2"
                  title="Remove file"
                >
                  <Trash2Icon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
